/**
 * Weekly Plan Generator — backend plan reader + converter.
 *
 * Read-only client: fetches the current-week plan from the backend and converts
 * the response into the frontend Plan shape. Plan *generation* is owned by the
 * backend (goal_plan_connector, weekly_plan_synthesizer, weekly_plan_catchup,
 * and the recalibration weekly_plan_recovery action) — the client never
 * generates plans anymore.
 */

import { Plan } from '../types/DataTypes';
import { storage } from './storage';
import { API_BASE_URL } from '../utils/api'; // Centralized environment-aware API URL

interface WeeklyPlanSummary {
  id: string;
  title: string;
  description: string;
  week_start_date: string;
  week_end_date: string;
  plan_type: string;
  status: string;
  goal_ids: string[];
  days_in_plan: number;
  progress_summary: {
    completion_rate: number;
    days_completed: number;
    total_days: number;
    current_day: number;
  };
}

interface PlanDetailResponse {
  id: string;
  title: string;
  description: string;
  week_overview?: string; // 🎯 JAN 2025: 2-sentence briefing (Part 5)
  plan_data: any;
  week_start_date: string;
  week_end_date: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export class WeeklyPlanGenerator {
  /**
   * 🔍 Get current week's plan from backend
   */
  static async getCurrentWeekPlan(): Promise<Plan | null> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/plans/current-week`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // No current plan exists
        }
        throw new Error(`Failed to get current week plan: ${response.status}`);
      }

      const planSummary: WeeklyPlanSummary | null = await response.json();
      
      // 🔧 BUG FIX: Check for null plan before accessing properties
      if (!planSummary || !planSummary.id) {
        return null; // No current plan exists
      }
      
      // Fetch detailed plan data
      const planDetail = await this.getPlanDetail(planSummary.id);
      
      return planDetail ? this.convertBackendPlanToFrontend(planDetail) : null;

    } catch (error) {
      console.error('❌ Failed to get current week plan:', error);
      return null;
    }
  }

  /**
   * 📋 Get detailed plan information
   */
  static async getPlanDetail(planId: string): Promise<PlanDetailResponse | null> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/plans/${planId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get plan detail: ${response.status}`);
      }

      return await response.json();

    } catch (error) {
      console.error('❌ Failed to get plan detail:', error);
      return null;
    }
  }

  /**
   * 🔄 Adapt plan based on user feedback
   */
  static async adaptPlanFromFeedback(planId: string, feedbackContent: string): Promise<boolean> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${API_BASE_URL}/api/plans/${planId}/adapt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ feedback_content: feedbackContent })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Plan adaptation failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Plan adapted successfully:', result);
      
      return true;

    } catch (error) {
      console.error('❌ Plan adaptation failed:', error);
      return false;
    }
  }

  /**
   * 🔐 Get authentication token
   */
  private static async getAuthToken(): Promise<string | null> {
    // Primary: shared storage (AsyncStorage/native or localStorage/web)
    const stored = await storage.getItem('jwt_token');
    if (stored) return stored;

    const adminToken = await storage.getItem('admin_token');
    if (adminToken) return adminToken;

    // Fallback: cookies (web only)
    if (typeof document !== 'undefined') {
      const cookieToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('jwt_token='))
        ?.split('=')[1];
      if (cookieToken) return cookieToken;
    }

    return null;
  }

  /**
   * 🔄 Convert backend plan format to frontend Plan interface
   * Made public for use by DataService when fetching plans from /api/plans
   */
  public static convertBackendPlanToFrontend(backendPlan: PlanDetailResponse): Plan {
    const planData = backendPlan.plan_data;
    
    return {
      id: backendPlan.id,
      title: backendPlan.title,
      description: backendPlan.description,
      weekOverview: backendPlan.week_overview, // 🎯 JAN 2025: 2-sentence briefing (Part 5)
      goalIds: planData.goal_ids || [],
      weekNumber: this.calculateWeekNumber(new Date(backendPlan.week_start_date)),
      totalWeeks: 52, // Assume yearly planning
      weekStartDate: new Date(backendPlan.week_start_date),
      weekEndDate: new Date(backendPlan.week_end_date),
      duration: `${planData.days_in_plan || 7} days`,
      state: backendPlan.status === 'active' ? 'active' : 'completed',
      progress: this.calculateProgress(planData),
      createdAt: new Date(backendPlan.created_at),
      updatedAt: new Date(backendPlan.updated_at),
      workoutIds: [], // Would be populated from daily_structure
      mobilityIds: [], // Would be populated from daily_structure
      
      smartDescription: {
        template: planData.integration_source === 'multi-goal-integrated' ? 'multi-goal-integrated' : 'single-goal',
        weeklyTheme: planData.weekly_theme || 'Weekly Progress',
        primaryFocus: planData.primary_focus || 'Fitness Development',
        secondaryFocus: planData.secondary_focus,
        goalContext: {
          goalIds: planData.goal_ids || [],
          goalTypes: ['fitness'], // Would be derived from goals
          goalStages: ['building'],
          synergies: planData.synergy_analysis ? [planData.synergy_analysis] : []
        },
        adaptationContext: {
          userProgress: 'on-track',
          recentFeedback: 'neutral',
          energyLevel: 'moderate',
          timeAvailability: 'normal'
        },
        generatedTitle: backendPlan.title,
        generatedDescription: backendPlan.description,
        weeklyInsight: planData.weekly_insights?.[0] || 'Focus on consistent progress',
        motivationalHook: 'Keep building momentum!'
      },
      
      integrationSource: planData.integration_source === 'multi-goal-integrated' ? 'multi-goal-integrated' : 'single-goal',
      goalContributions: planData.goal_contributions || [],
      weeklyFocus: {
        primary: planData.primary_focus || 'Fitness Development',
        secondary: planData.secondary_focus,
        notes: planData.goal_integration_strategy
      }
    };
  }

  /**
   * 📊 Calculate plan progress
   */
  private static calculateProgress(planData: any): number {
    const dailyStructure = planData.daily_structure || {};
    const totalDays = Object.keys(dailyStructure).length;
    
    if (totalDays === 0) return 0;
    
    // Mock progress - in real app, this would check actual completion status
    const completedDays = Math.min(3, totalDays);
    return Math.round((completedDays / totalDays) * 100);
  }

  /**
   * 📅 Calculate week number
   */
  private static calculateWeekNumber(date: Date): number {
    const start = new Date(date.getFullYear(), 0, 1);
    const diff = date.getTime() - start.getTime();
    return Math.ceil(diff / (7 * 24 * 60 * 60 * 1000));
  }

} 
